import React, { useState } from 'react';
import { Box, Button, Modal, Typography } from '@mui/material';
import { AntSwitch } from '../../../components/antSwitch';
import { useNavigate } from 'react-router-dom';
import DeleteModal from './deleteModal';
import StatusModal from './statusModal';
import { tableDeleteButtonSx, tableViewButtonSx } from '../../../styles/actionStyles';

const textCellSx = {
  fontSize: 16,
  lineHeight: '24px',
  letterSpacing: '0.15px',
  color: '#1C1B1F',
};

export default function AppCatalogueColumn(props) {
  const navigate = useNavigate();
  const { setAlert, setOpenAlert, setResponseMsg, setLoading, getAppCatalogueData } = props;
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const ALERT_DURATION = 2000;

  const alertClose = () => {
    setAlert('');
  };

  const handleOpenDeleteModal = (id, status) => {
    setSelectedId(id);
    setSelectedStatus(status);
    setOpenDeleteModal(true);
  };

  const handleOpenStatusModal = (id, status) => {
    setSelectedId(id);
    setSelectedStatus(status);
    setOpenStatusModal(true);
  };

  const appCatalogueColumns = [
    {
      field: 'name',
      headerName: 'App Name',
      flex: 1,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ ...textCellSx }}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'appKey',
      headerName: 'App Key',
      flex: 0.9,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => (
        <Typography
          sx={{
            ...textCellSx,
            fontFamily: 'monospace',
            fontSize: 14,
            color: '#294190',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={params.value}
        >
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'url',
      headerName: 'URL',
      flex: 1.2,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => (
        <Typography
          sx={{
            ...textCellSx,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: '#4F8506',
            textDecoration: 'underline',
          }}
          title={params.value}
        >
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1.5,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => {
        const plainText = String(params.value || '')
          .replace(/<[^>]+>/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        return (
          <Typography
            sx={{
              ...textCellSx,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={plainText}
          >
            {plainText || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'serviceCount',
      headerName: 'App Services',
      flex: 0.6,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ ...textCellSx, fontWeight: 500 }}>
          {params.value ?? '-'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => {
        const isActive = params.row.status === 'ACTIVE';
        return (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
              <AntSwitch
                checked={isActive}
                onChange={() => handleOpenStatusModal(params.row.id, params.row.status)}
                inputProps={{ 'aria-label': 'controlled' }}
              />
              <Typography
                sx={{
                  fontSize: 16,
                  lineHeight: '24px',
                  letterSpacing: '0.15px',
                  fontWeight: 500,
                  color: isActive ? '#4F8506' : '#625B71',
                }}
              >
                {isActive ? 'Active' : 'Inactive'}
              </Typography>
            </Box>
            <Modal
              open={openStatusModal}
              onClose={() => setOpenStatusModal(false)}
              BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
            >
              <div>
                <StatusModal
                  onClose={() => setOpenStatusModal(false)}
                  getAppCatalogueData={getAppCatalogueData}
                  selectedId={selectedId}
                  selectedStatus={selectedStatus}
                  alertClose={alertClose}
                  setOpenAlert={setOpenAlert}
                  setResponseMsg={setResponseMsg}
                  setAlert={setAlert}
                  ALERT_DURATION={ALERT_DURATION}
                  setLoading={setLoading}
                />
              </div>
            </Modal>
          </>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 1,
      minWidth: 250,
      sortable: false,
      renderCell: (params) => (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
            <Button
              onClick={() => navigate(`/appCatalogueView/${params.row.id}?type=edit`)}
              startIcon={<span className="material-symbols-rounded">visibility</span>}
              sx={tableViewButtonSx}
            >
              View
            </Button>
            <Button
              onClick={() => handleOpenDeleteModal(params.row.id, params.row.status)}
              startIcon={<span className="material-symbols-rounded">delete</span>}
              sx={{ ...tableDeleteButtonSx, minWidth: 96 }}
            >
              Delete
            </Button>
          </Box>
          <Modal
            open={openDeleteModal}
            onClose={() => setOpenDeleteModal(false)}
            BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
          >
            <div>
              <DeleteModal
                onClose={() => setOpenDeleteModal(false)}
                getAppCatalogueData={getAppCatalogueData}
                selectedId={selectedId}
                selectedStatus={selectedStatus}
                alertClose={alertClose}
                setOpenAlert={setOpenAlert}
                setResponseMsg={setResponseMsg}
                setAlert={setAlert}
                ALERT_DURATION={ALERT_DURATION}
                setLoading={setLoading}
              />
            </div>
          </Modal>
        </>
      ),
    },
  ];

  return appCatalogueColumns;
}
